import CustomPagination from "components/CustomPagination";
import Loading from "components/Loading";
import { useAuth } from "context/AuthContext";
import { useTooltip } from "context/TooltipProvider";
import ChangeOfVenueForm from "pages/InstituteOfCharge/modal/ChangeOfVenueForm";
import React, { useEffect, useState } from "react";
import { fetchChangeOfVenueByCRNumberAndIncidentRegNo } from "services/caseServices";
import { ChangeOfVenue, ChangeOfVenueStatus, Roles } from "types";
import { errorHandler, hasRole } from "utils/helper";

type Props = {
  crimeReferenceNumber?: string;
  incidentRegistrationNumber?: string;
  setSelectedId: React.Dispatch<React.SetStateAction<string | number>>;
  setPageType: React.Dispatch<React.SetStateAction<"list" | "detail">>;
};

const ITEMS_PER_PAGE = 9;

const ChangeOfVenueList: React.FC<Props> = ({
  crimeReferenceNumber,
  incidentRegistrationNumber,
  setSelectedId,
  setPageType,
}) => {
  const { showTooltip } = useTooltip();
  const { loginData } = useAuth();

  const [venues, setVenues] = useState<ChangeOfVenue[]>([]);
  const [loading, setLoading] = useState(true);

  const [editVenue, setEditVenue] = useState<ChangeOfVenue | null>(null);
  const [showEditDialog, setShowEditDialog] = useState(false);

  const isProsecutor = hasRole(loginData?.roles, [Roles.prosecutor]);

  const [currentPage, setCurrentPage] = useState(0);
  // const navigate = useNavigate();

  const fetchData = () => {
    setLoading(true);
    fetchChangeOfVenueByCRNumberAndIncidentRegNo(
      crimeReferenceNumber || "",
      incidentRegistrationNumber || ""
    )
      .then((data: ChangeOfVenue[]) => {
        setVenues(data);
        setLoading(false);
      })
      .catch((error) => {
        showTooltip(errorHandler(error));
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, []);

  // Pagination logic
  const pageCount = Math.ceil(venues.length / ITEMS_PER_PAGE);
  const paginated = venues.slice(
    currentPage * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE + ITEMS_PER_PAGE
  );

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  return (
    <div className="max-w-screen-xl space-y-6">
      <div className="max-w-screen-xl ml-1 mr-4 space-y-6">
        <div className="overflow-hidden bg-white shadow rounded-xl">
          {loading ? (
            <Loading label="Loading Change of Venue Requests" />
          ) : paginated.length === 0 ? (
            <div className="text-center text-gray-500 py-8">
              No change of venue requests found.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 p-4">
              {paginated.map((item, idx) => (
                <div
                  key={item.id || idx}
                  className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 flex flex-col gap-2 transition-all duration-150 hover:shadow-lg"
                >
                  <div className="flex flex-row justify-between items-center mb-2">
                    <span className="font-semibold text-[#AA232F] text-base">
                      {item.registration_number || "No Reg. #"}
                    </span>
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-semibold shadow-sm
                            ${
                              item.status === "pending"
                                ? "bg-yellow-50 text-yellow-800 border border-yellow-200"
                                : item.status === "approved"
                                ? "bg-green-50 text-green-800 border border-green-200"
                                : item.status === "forwarded_to_coordinator"
                                ? "bg-blue-50 text-blue-800 border border-blue-200"
                                : item.status === "respond_back_to_prosecutor"
                                ? "bg-red-50 text-red-800 border border-red-200"
                                : "bg-gray-50 text-gray-700 border border-gray-200"
                            }
                          `}
                    >
                      {
                        // Show the display value from ChangeOfVenueStatus if available
                        ChangeOfVenueStatus[
                          item.status as keyof typeof ChangeOfVenueStatus
                        ] || item.status
                      }
                    </span>
                  </div>
                  <div className="flex flex-col gap-1 mb-2">
                    <span className="text-sm text-gray-700">
                      <span className="font-semibold">Prosecution Case #:</span>{" "}
                      {item.prosecution_case_number || "-"}
                    </span>
                    <span className="text-sm text-gray-700">
                      <span className="font-semibold">Court Case #:</span>{" "}
                      {item.court_case_number || "-"}
                    </span>
                    <span className="text-sm text-gray-700">
                      <span className="font-semibold">Date:</span>{" "}
                      {item.date
                        ? new Date(item.date).toLocaleDateString()
                        : "-"}
                    </span>
                    <span className="text-sm text-gray-700">
                      <span className="font-semibold">Reason:</span>{" "}
                      {item.reason_summary || "-"}
                    </span>
                  </div>
                  <div className="flex flex-row justify-end gap-2 mt-2">
                    {/* {item?.summary_file_info?.file_url && (
                      <a
                        href={item.summary_file_info?.file_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-4 py-1 text-sm bg-[#AA232F] text-white rounded hover:bg-[#212121] transition"
                        download
                      >
                        Download File
                      </a>
                    )} */}
                    <div className="flex flex-row gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedId(item?.id || "");
                          setPageType("detail");
                        }}
                        className="px-4 py-1 text-sm bg-[#212121] text-white rounded hover:bg-[#AA232F] transition"
                      >
                        View
                      </button>
                      {isProsecutor &&
                        item?.status === "respond_back_to_prosecutor" && (
                          <button
                            type="button"
                            onClick={() => {
                              setEditVenue(item);
                              setShowEditDialog(true);
                            }}
                            className="px-4 py-1 text-sm bg-[#212121] text-white rounded hover:bg-[#AA232F] transition"
                          >
                            Edit
                          </button>
                        )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          {pageCount > 1 && (
            <div className="flex justify-center my-8">
              <CustomPagination
                pageCount={pageCount}
                currentPage={currentPage}
                onPageChange={handlePageChange}
              />
            </div>
          )}
        </div>
      </div>
      {showEditDialog && editVenue && (
        <ChangeOfVenueForm
          venue={editVenue}
          onClose={(refetch) => {
            setShowEditDialog(false);
            setEditVenue(null);
            if (refetch) fetchData();
          }}
        />
      )}
    </div>
  );
};

export default ChangeOfVenueList;
